import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  IconButton,
  Input,
  Text,
  Alert,
  AlertIcon,
  VStack,
  HStack,
  Heading,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { EditIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { FaCamera } from 'react-icons/fa';
import { useProfile, displayPhoneNumber, formatPhoneNumber } from '../hooks/useProfile';
import { useRoleRequests } from '../hooks/useRoleRequests';
import { useUser } from '@supabase/auth-helpers-react';

export default function Profile() {
  const navigate = useNavigate();
  const toast = useToast();
  const user = useUser();
  const {
    profile,
    permissions,
    loading: profileLoading,
    error: profileError,
    updateProfile,
    updateAvatar,
    refreshProfile,
  } = useProfile();

  const {
    pendingRequests,
    loading: requestsLoading,
    error: requestsError,
    createRoleRequest,
  } = useRoleRequests();

  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
  });

  React.useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone_number: profile.phone_number || '',
      });
    }
  }, [profile]);

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      phone_number: profile.phone_number || '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneNumberChange = (e) => {
    const formattedNumber = formatPhoneNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      phone_number: formattedNumber
    }));
  };

  const handleSave = async () => {
    try {
      await updateProfile(formData);
      setEditing(false);
      refreshProfile();
      toast({
        title: "Profile updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error updating profile",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        await updateAvatar(file);
        refreshProfile();
        toast({
          title: "Avatar updated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error updating avatar:', error);
        toast({
          title: "Error updating avatar",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const RoleRequestSection = () => {
    const { createRoleRequest, pendingRequests } = useRoleRequests();
    const toast = useToast();

    const handleRequestRole = async (role) => {
      try {
        const { error } = await createRoleRequest(role);
        if (error) throw error;

        toast({
          title: 'Role request submitted',
          description: `Your request for ${role} role has been submitted for review.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Error submitting request',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    const isPendingRequest = (role) => {
      return pendingRequests.some(req => req.requested_role === role);
    };

    return (
      <Card>
        <CardHeader>
          <Heading size="md">Role Requests</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text mb={2}>Request additional roles:</Text>
              <HStack spacing={4}>
                <Button
                  colorScheme="red"
                  isDisabled={profile?.is_administrator || isPendingRequest('administrator')}
                  onClick={() => handleRequestRole('administrator')}
                >
                  Request Administrator Role
                </Button>
                <Button
                  colorScheme="blue"
                  isDisabled={profile?.is_message_sender || isPendingRequest('message_sender')}
                  onClick={() => handleRequestRole('message_sender')}
                >
                  Request Message Sender Role
                </Button>
              </HStack>
            </Box>
            {pendingRequests.length > 0 && (
              <Box>
                <Text fontWeight="bold" mb={2}>Pending Requests:</Text>
                {pendingRequests.map((request) => (
                  <Badge
                    key={request.id}
                    colorScheme={request.requested_role === 'administrator' ? 'red' : 'blue'}
                    mr={2}
                  >
                    {request.requested_role} (pending)
                  </Badge>
                ))}
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>
    );
  };

  if (profileLoading || requestsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress isIndeterminate />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box p={4}>
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Alert status="info">
                <AlertIcon />
                <VStack align="stretch" spacing={4}>
                  <Text>Please complete your profile setup to continue.</Text>
                  <Button
                    colorScheme="blue"
                    onClick={() => navigate('/profile-setup')}
                  >
                    Complete Profile Setup
                  </Button>
                </VStack>
              </Alert>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Card>
        <CardHeader>
          <HStack justify="space-between" align="center">
            <Heading size="md">Profile</Heading>
            {!editing ? (
              <IconButton
                icon={<EditIcon />}
                onClick={handleEdit}
                aria-label="Edit profile"
                variant="ghost"
              />
            ) : (
              <HStack>
                <IconButton
                  icon={<CheckIcon />}
                  onClick={handleSave}
                  colorScheme="green"
                  aria-label="Save changes"
                />
                <IconButton
                  icon={<CloseIcon />}
                  onClick={handleCancel}
                  colorScheme="red"
                  aria-label="Cancel editing"
                />
              </HStack>
            )}
          </HStack>
        </CardHeader>
        <Divider />
        <CardBody>
          {(profileError || requestsError) && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {profileError || requestsError}
            </Alert>
          )}
          <VStack spacing={6} align="stretch">
            <Box textAlign="center">
              <Avatar
                size="xl"
                name={`${profile.first_name} ${profile.last_name}`}
                src={profile.avatar_url}
                mb={2}
              />
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
                id="avatar-upload"
              />
              <label htmlFor="avatar-upload">
                <Button
                  as="span"
                  leftIcon={<FaCamera />}
                  size="sm"
                  variant="outline"
                >
                  Change Photo
                </Button>
              </label>
            </Box>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <FormControl>
                  <FormLabel>First Name</FormLabel>
                  {editing ? (
                    <Input
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                    />
                  ) : (
                    <Text>{profile.first_name}</Text>
                  )}
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Last Name</FormLabel>
                  {editing ? (
                    <Input
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                    />
                  ) : (
                    <Text>{profile.last_name}</Text>
                  )}
                </FormControl>
              </GridItem>

              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Text>{user?.email}</Text>
                </FormControl>
              </GridItem>

              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel>Phone Number</FormLabel>
                  {editing ? (
                    <Input
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handlePhoneNumberChange}
                      placeholder="+1 (555) 555-5555"
                    />
                  ) : (
                    <Text>{displayPhoneNumber(profile.phone_number)}</Text>
                  )}
                </FormControl>
              </GridItem>

              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel>Current Roles</FormLabel>
                  <HStack spacing={2}>
                    {profile.is_superadmin && (
                      <Badge colorScheme="purple">Super Admin</Badge>
                    )}
                    {profile.is_administrator && (
                      <Badge colorScheme="blue">Administrator</Badge>
                    )}
                    {profile.is_message_sender && (
                      <Badge colorScheme="green">Message Sender</Badge>
                    )}
                    {!profile.is_superadmin && !profile.is_administrator && !profile.is_message_sender && (
                      <Text color="gray.500">No special roles</Text>
                    )}
                  </HStack>
                </FormControl>
              </GridItem>

              <GridItem colSpan={2}>
                <RoleRequestSection />
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Created</FormLabel>
                  <Text>{formatDate(profile.created_at)}</Text>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Last Updated</FormLabel>
                  <Text>{formatDate(profile.updated_at)}</Text>
                </FormControl>
              </GridItem>
            </Grid>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
}
